class GeoRedirectBanner extends HTMLElement {
  constructor() {
    super();

    // localStorage key + dismiss cooldown (7 days in ms)
    this.storageKey = "wt-geo-banner-dismissed";
    this.dismissCooldownMs = 7 * 24 * 60 * 60 * 1000;

    // IP lookup endpoint (free, CORS-enabled). Swap if needed.
    this.ipEndpoint = "https://ipapi.co/json/";

    this.config = [];
    this.banner = this;
    this.textEl = this.querySelector("[data-geo-banner-text]");
    this.flagEl = this.querySelector("[data-geo-banner-flag]");
    this.linkEl = this.querySelector("[data-geo-banner-link]");
    this.dismissBtn = this.querySelector("[data-geo-banner-dismiss]");
    this.configScript = this.querySelector("[data-geo-banner-config]");
  }

  connectedCallback() {
    if (!this.configScript) return;

    try {
      this.config = JSON.parse(this.configScript.textContent || "[]");
    } catch (err) {
      console.warn("[geo-redirect-banner] Failed to parse config:", err);
      return;
    }

    // Bail out if the visitor already dismissed recently
    if (this.isRecentlyDismissed()) return;

    // Bail out if there is no "current" store configured — we can't
    // determine whether the visitor is on the wrong store.
    const currentStore = this.config.find((s) => s.current === true);
    if (!currentStore) return;

    this.attachDismissHandler();
    this.detectAndShow(currentStore);
  }

  // ---------------------------------------------------------------
  // IP detection
  // ---------------------------------------------------------------
  async detectAndShow(currentStore) {
    try {
      const res = await fetch(this.ipEndpoint, {
        method: "GET",
        credentials: "omit",
        cache: "no-store",
      });
      if (!res.ok) return;
      const data = await res.json();
      const visitorIso = (data && data.country_code) || "";
      if (!visitorIso) return;

      // Find a matching store by country ISO code
      const match = this.config.find(
        (s) => s.countryIso && s.countryIso.toUpperCase() === visitorIso.toUpperCase(),
      );

      // No matching store OR visitor is already on the right store -> hide
      if (!match || match.url === currentStore.url) return;

      this.render(currentStore, match);
    } catch (err) {
      // Network/CORS failure: fail silently — do not block the UX
      console.warn("[geo-redirect-banner] IP detection failed:", err);
    }
  }

  // ---------------------------------------------------------------
  // Render the banner with localized copy
  // ---------------------------------------------------------------
  render(currentStore, match) {
    if (!this.textEl || !this.linkEl) return;

    // Use the locale's message template if present; otherwise build a
    // sensible fallback. The template supports two placeholders:
    //   {{visitor_country}}  -> name of the IP-detected country
    //   {{visitor_language}} -> language of the matched store
    //   {{current_country}}  -> name of the current store's country
    const tpl = window.geoRedirectBannerI18n?.message ||
      "You appear to be in {{visitor_country}}. Visit our {{visitor_country}} store in {{visitor_language}}.";
    const msg = tpl
      .replaceAll("{{visitor_country}}", match.country)
      .replaceAll("{{visitor_language}}", match.language)
      .replaceAll("{{current_country}}", currentStore.country);

    this.textEl.textContent = msg;

    // Flag
    if (this.flagEl) {
      this.flagEl.innerHTML = "";
      // Render a tiny flag via Shopify CDN (flag-icon snippet not
      // available in JS — fall back to a country code badge instead).
      const badge = document.createElement("span");
      badge.textContent = match.countryIso.toUpperCase();
      badge.style.fontWeight = "700";
      badge.style.fontSize = "1.1rem";
      badge.style.letterSpacing = "0.04em";
      this.flagEl.appendChild(badge);
    }

    // CTA link
    this.linkEl.href = match.url;
    const ctaTpl = window.geoRedirectBannerI18n?.cta || "Go to {{visitor_country}}";
    this.linkEl.textContent = ctaTpl.replaceAll("{{visitor_country}}", match.country);

    // Reveal
    this.banner.hidden = false;
    requestAnimationFrame(() => {
      this.banner.classList.add("is-visible");
    });
  }

  // ---------------------------------------------------------------
  // Dismiss handling
  // ---------------------------------------------------------------
  attachDismissHandler() {
    if (!this.dismissBtn) return;
    this.dismissBtn.addEventListener("click", (e) => {
      e.preventDefault();
      this.dismiss();
    });
  }

  dismiss() {
    this.banner.classList.remove("is-visible");
    try {
      localStorage.setItem(this.storageKey, String(Date.now()));
    } catch (err) {
      // localStorage may be unavailable (private mode, etc.) — ignore
    }
    // Hide after the collapse animation completes
    setTimeout(() => {
      this.banner.hidden = true;
    }, 420);
  }

  isRecentlyDismissed() {
    try {
      const ts = localStorage.getItem(this.storageKey);
      if (!ts) return false;
      const dismissedAt = parseInt(ts, 10);
      if (Number.isNaN(dismissedAt)) return false;
      return Date.now() - dismissedAt < this.dismissCooldownMs;
    } catch (err) {
      return false;
    }
  }
}

customElements.define("geo-redirect-banner", GeoRedirectBanner);
